import { Suspense, lazy } from 'react'
import { Loader2, ShieldAlert } from 'lucide-react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { Layout } from './components/Layout'
import { StepUpProvider } from './components/StepUp'
import { EmptyState, ErrorBlock } from './components/states'
import { Login } from './pages/Login'
import { Schools } from './pages/Schools'
import { useSession } from './lib/session'

/** The curriculum builder is the heaviest screen here and the least used. */
const Catalogues = lazy(() => import('./pages/Catalogues').then((m) => ({ default: m.Catalogues })))
const CatalogueBuilder = lazy(() =>
  import('./pages/CatalogueBuilder').then((m) => ({ default: m.CatalogueBuilder })),
)
const SchoolDetail = lazy(() =>
  import('./pages/SchoolDetail').then((m) => ({ default: m.SchoolDetail })),
)
const PlatformAdmins = lazy(() =>
  import('./pages/PlatformAdmins').then((m) => ({ default: m.PlatformAdmins })),
)
const Signups = lazy(() => import('./pages/Signups').then((m) => ({ default: m.Signups })))
const NotBuilt = lazy(() => import('./pages/NotBuilt').then((m) => ({ default: m.NotBuilt })))

function Spinner({ full = false }: { full?: boolean }) {
  return (
    <div
      className={`flex items-center justify-center text-faint ${
        full ? 'min-h-screen bg-bg' : 'min-h-[60vh]'
      }`}
    >
      <Loader2 size={22} className="animate-spin" />
    </div>
  )
}

export default function App() {
  const { loading, isSignedIn, isPlatformAdmin, error, retry, signOut } = useSession()

  if (loading) return <Spinner full />
  if (!isSignedIn) return <Login />

  // A failed *check* is not the same as being refused. Saying "no access" when the
  // API was simply unreachable sends someone hunting for a permissions bug.
  if (error) {
    return (
      <div className="mx-auto max-w-lg p-6">
        <ErrorBlock error={new Error(error)} onRetry={retry} />
      </div>
    )
  }

  if (!isPlatformAdmin) {
    return (
      <div className="mx-auto max-w-lg p-6">
        <EmptyState
          icon={<ShieldAlert size={22} />}
          title="No platform access"
          body="This account signed in fine, but it isn't a platform admin. If you need access, ask someone who already has it to grant it."
          action={
            <button
              onClick={() => void signOut()}
              className="text-sm font-semibold text-accent hover:text-accent"
            >
              Sign in with a different account
            </button>
          }
        />
      </div>
    )
  }

  return (
    <StepUpProvider>
      <Layout>
        <Suspense fallback={<Spinner />}>
          <Routes>
            <Route path="/" element={<Navigate to="/schools" replace />} />
            <Route path="/schools" element={<Schools />} />
            <Route path="/schools/:schoolId" element={<SchoolDetail />} />
            <Route path="/curriculums" element={<Catalogues />} />
            <Route path="/curriculums/:catalogueId" element={<CatalogueBuilder />} />
            <Route path="/waitlist" element={<Signups />} />
            <Route path="/platform-admins" element={<PlatformAdmins />} />
            <Route path="/not-built" element={<NotBuilt />} />
          </Routes>
        </Suspense>
      </Layout>
    </StepUpProvider>
  )
}
